import { useState, useRef, useCallback, useEffect } from 'react';
import { notification } from 'antd';
import { database, storage } from '../../../firebase';
import { ref, update, get } from 'firebase/database';
import { ref as storageRef, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { ENVIRONMENT } from '../../../environment';

const soapNoteAiBaseUrl = ENVIRONMENT === 'production'
    ? 'https://northamerica-northeast1-yourdoctorsonline-prod.cloudfunctions.net'
    : 'https://northamerica-northeast1-yourdoctorsonline-staging.cloudfunctions.net';

const SOAP_NOTE_AI_ENDPOINT = `${soapNoteAiBaseUrl}/soapNotesAiPostMeeting`;

const DEEPGRAM_URL = 'wss://api.deepgram.com/v1/listen?model=nova-3-medical&max_speakers=2&smart_format=true&punctuate=true&diarize=true&language=en-US';

// Reconnect waits 1s, 2s, 4s, 8s, then every 10s for as long as the call lasts.
const reconnectDelay = (attempt) => Math.min(1000 * 2 ** (attempt - 1), 10000);

// The call audio is saved while the call runs: the whole recording so far, overwriting one file,
// every 20s at first and less often as the file grows (a quarter of the elapsed time).
const UPLOAD_CHECK_MS = 5000;
const uploadInterval = (elapsedMs) => Math.max(20000, elapsedMs / 4);

const soapNoteAIMeeting = async (payload) => {
    const response = await fetch(SOAP_NOTE_AI_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => 'Failed to process AI request.');
        throw new Error(errorText || 'Failed to process AI request.');
    }

    try {
        return await response.json();
    } catch (error) {
        return null;
    }
};

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const useAgoraTranscription = ({ conversationId, doctorDetail, callType, transcriptMessageKey }) => {
    // isTranscribing: a transcription session is running for the call (even while reconnecting).
    // isLive: the Deepgram connection is open right now.
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isLive, setIsLive] = useState(false);
    const [liveTranscript, setLiveTranscript] = useState('');
    const [interimTranscript, setInterimTranscript] = useState('');
    const DEEPGRAM_KEY = "f523c929c549d870333ac201338fc8164d44c092";

    const socketRef = useRef(null);
    const audioContextRef = useRef(null);
    const liveRecorderRef = useRef(null);    // feeds Deepgram; recreated on every (re)connect
    const archiveRecorderRef = useRef(null); // the call recording; one per session, never restarted
    const archiveChunksRef = useRef([]);
    const chunkCounterRef = useRef(0);
    const messageIdRef = useRef(null);
    const sessionStartTimeRef = useRef(0);
    const sessionCounterRef = useRef(0); // increments each start/stop cycle, never resets mid-conversation

    // Everything a session writes is pinned at start, so switching to another chat mid-call
    // cannot move or stop it.
    const sessionActiveRef = useRef(false);
    const stoppingRef = useRef(false);
    const convIdRef = useRef(null);
    const callTypeRef = useRef(null);
    const doctorRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const reconnectTimerRef = useRef(null);
    const announcedRef = useRef(false);
    const appendQueueRef = useRef(Promise.resolve());
    const uploadTimerRef = useRef(null);
    const uploadingRef = useRef(null);
    const lastUploadAtRef = useRef(0);

    const localStreamRef = useRef(null);
    const remoteStreamRef = useRef(null);
    const mergerRef = useRef(null);
    const destinationRef = useRef(null);
    const keepaliveIntervalRef = useRef(null);
    const transcriptTimeoutRef = useRef(null);
    const lastFinalTranscriptRef = useRef('');

    // Reset for a new conversation, but never under a running session (a chat switch mid-call).
    useEffect(() => {
        if (sessionActiveRef.current) return;
        messageIdRef.current = null;
        sessionCounterRef.current = 0;
    }, [conversationId]);

    useEffect(() => {
        if (transcriptMessageKey && !sessionActiveRef.current) {
            messageIdRef.current = transcriptMessageKey;
        }
    }, [transcriptMessageKey]);

    const transcriptPath = () => `/mentalHealthTranscript/${convIdRef.current}/${messageIdRef.current}`;

    const storeAudioChunkToFirebase = useCallback(async (audioChunks, chunkOrder, cid, messageId, kind) => {
        if (!audioChunks?.length || !cid || !messageId) return;

        const formattedOrder = String(chunkOrder).padStart(2, '0');
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm;codecs=opus' });
        if (audioBlob.size < 1000) return;

        const storagePath = `mentalHealthTranscript/${cid}/${messageId}/chunk_${formattedOrder}.webm`;
        const audioRef = storageRef(storage, storagePath);

        await new Promise((resolve, reject) => {
            uploadBytesResumable(audioRef, audioBlob).on('state_changed', null, reject, resolve);
        });
        const downloadURL = await getDownloadURL(audioRef);

        await update(ref(database, `/mentalHealthTranscript/${cid}/${messageId}/chunks/${formattedOrder}`), {
            callType: kind || null,
            url: downloadURL,
            timestamp: Date.now(),
            size: audioBlob.size,
            duration: (audioBlob.size * 8) / 128000, // approximate, at 128 kbps
            order: chunkOrder,
            status: 'processed',
            uploadedAt: Date.now()
        });
        console.log(`✓ Call audio saved (${audioBlob.size} bytes) to ${storagePath}`);
    }, []);

    // Upload the recording so far, unless an upload is already running. Never throws.
    const saveRecordingSoFar = useCallback(() => {
        if (uploadingRef.current || !archiveChunksRef.current.length) return uploadingRef.current;
        lastUploadAtRef.current = Date.now();
        uploadingRef.current = storeAudioChunkToFirebase(archiveChunksRef.current.slice(), sessionCounterRef.current,
            convIdRef.current, messageIdRef.current, callTypeRef.current)
            .catch((error) => console.error('❌ Call audio upload failed:', error))
            .finally(() => { uploadingRef.current = null; });
        return uploadingRef.current;
    }, [storeAudioChunkToFirebase]);

    const startLiveRecorder = useCallback(() => {
        const socket = socketRef.current;
        if (!destinationRef.current || !socket || socket.readyState !== WebSocket.OPEN) return;
        if (liveRecorderRef.current && liveRecorderRef.current.state !== 'inactive') return;
        try {
            // A fresh recorder per connection: each Deepgram stream needs its own WebM header.
            const recorder = new MediaRecorder(destinationRef.current.stream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 128000
            });
            recorder.addEventListener('dataavailable', (event) => {
                if (event.data.size > 0 && socketRef.current === socket && socket.readyState === WebSocket.OPEN) {
                    socket.send(event.data);
                }
            });
            recorder.addEventListener('error', (event) => console.error('❌ Live recorder error:', event.error));
            recorder.start(250);
            liveRecorderRef.current = recorder;
        } catch (error) {
            console.error('❌ Could not start the live recorder:', error);
        }
    }, []);

    const stopLiveRecorder = () => {
        const recorder = liveRecorderRef.current;
        liveRecorderRef.current = null;
        if (recorder && recorder.state !== 'inactive') {
            try { recorder.stop(); } catch (e) {}
        }
    };

    const startArchiveRecorder = useCallback(() => {
        if (!destinationRef.current || archiveRecorderRef.current) return;
        try {
            const recorder = new MediaRecorder(destinationRef.current.stream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 128000
            });
            recorder.addEventListener('dataavailable', (event) => {
                if (event.data.size > 0) archiveChunksRef.current.push(event.data);
            });
            recorder.addEventListener('error', (event) => console.error('❌ Recording error:', event.error));
            recorder.start(1000);
            archiveRecorderRef.current = recorder;

            lastUploadAtRef.current = Date.now();
            uploadTimerRef.current = setInterval(() => {
                const elapsed = Date.now() - sessionStartTimeRef.current;
                if (Date.now() - lastUploadAtRef.current >= uploadInterval(elapsed)) saveRecordingSoFar();
            }, UPLOAD_CHECK_MS);
        } catch (error) {
            console.error('❌ Could not start the call recording:', error);
        }
    }, [saveRecordingSoFar]);

    const updateAudioStreams = useCallback((localTrack, remoteTrack) => {
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            console.error("AudioContext not ready, cannot update streams");
            return false;
        }
        if (!localTrack && !remoteTrack) {
            console.error("No audio tracks provided");
            return false;
        }

        try {
            const audioContext = audioContextRef.current;
            if (audioContext.state === 'suspended') {
                audioContext.resume().catch(() => {});
            }

            // Create merger and destination only once
            if (!mergerRef.current) {
                mergerRef.current = audioContext.createChannelMerger(2);
                destinationRef.current = audioContext.createMediaStreamDestination();
                mergerRef.current.connect(destinationRef.current);
            }

            // Disconnect existing connections before creating new ones
            [localStreamRef, remoteStreamRef].forEach((streamRef) => {
                if (!streamRef.current) return;
                try {
                    streamRef.current.source?.disconnect();
                    streamRef.current.gain?.disconnect();
                    streamRef.current.panner?.disconnect();
                } catch (e) {}
                streamRef.current = null;
            });

            const connectTrack = (track, channel, gainValue, pan, streamRef, label) => {
                if (!track || typeof track.getMediaStreamTrack !== 'function') return;
                const mediaStreamTrack = track.getMediaStreamTrack();
                // A muted microphone is still connected: it carries silence while muted and the
                // doctor's voice again on unmute. Requiring `enabled` here dropped the doctor's voice
                // for the rest of the call whenever the patient's audio reconnected during a mute.
                if (mediaStreamTrack.readyState !== 'live') {
                    console.warn(`${label} track is not live:`, mediaStreamTrack.readyState);
                    return;
                }
                const source = audioContext.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
                const gain = audioContext.createGain();
                gain.gain.value = gainValue;
                const panner = audioContext.createStereoPanner();
                panner.pan.value = pan;
                source.connect(gain);
                gain.connect(panner);
                panner.connect(mergerRef.current, 0, channel);
                streamRef.current = { source, gain, panner, track: mediaStreamTrack };
                console.log(`✓ ${label} audio connected to channel ${channel}`);
            };

            connectTrack(localTrack, 0, 1.0, -0.3, localStreamRef, 'Local (microphone)');
            connectTrack(remoteTrack, 1, 0.8, 0.3, remoteStreamRef, 'Remote');

            startArchiveRecorder();
            startLiveRecorder();
            return true;
        } catch (error) {
            console.error("❌ Error updating audio streams:", error);
            return false;
        }
    }, [startArchiveRecorder, startLiveRecorder]);

    const handleDeepgramMessage = useCallback(async (message) => {
        try {
            const received = JSON.parse(message.data);
            const transcript = received.channel?.alternatives?.[0]?.transcript || '';
            if (transcript.trim() === '') return;

            if (received.is_final) {
                lastFinalTranscriptRef.current = transcript;
                setLiveTranscript(transcript);
                setInterimTranscript('');
                if (transcriptTimeoutRef.current) clearTimeout(transcriptTimeoutRef.current);
                transcriptTimeoutRef.current = setTimeout(() => setLiveTranscript(''), 7000);
            } else {
                setInterimTranscript(transcript);
            }

            const transcriptTimestamp = Date.now();
            const formattedOrder = String(chunkCounterRef.current).padStart(2, '0');
            const words = received.channel?.alternatives?.[0]?.words || [];

            const speakerSegments = [];
            let currentSpeaker = null;
            let currentText = '';
            words.forEach(word => {
                const speaker = word.speaker || 0;
                if (currentSpeaker !== speaker && currentText) {
                    speakerSegments.push({ speaker: `Speaker ${currentSpeaker}`, text: currentText.trim() });
                    currentText = '';
                }
                currentSpeaker = speaker;
                currentText += ` ${word.word}`;
            });
            if (currentText) {
                speakerSegments.push({ speaker: `Speaker ${currentSpeaker}`, text: currentText.trim() });
            }

            const basePath = transcriptPath();
            await update(ref(database, `${basePath}/chunks/${formattedOrder}/transcripts/${transcriptTimestamp}`), {
                text: transcript,
                timestamp: transcriptTimestamp,
                isFinal: received.is_final || false,
                speakerSegments,
                rawWords: words,
                callType: callTypeRef.current
            });

            if (received.is_final) {
                let formattedSegments = '';
                speakerSegments.forEach(segment => {
                    const relativeTime = transcriptTimestamp - sessionStartTimeRef.current;
                    const hours = Math.floor(relativeTime / 3600000).toString().padStart(2, '0');
                    const minutes = Math.floor((relativeTime % 3600000) / 60000).toString().padStart(2, '0');
                    const seconds = Math.floor((relativeTime % 60000) / 1000).toString().padStart(2, '0');
                    formattedSegments += `${hours}:${minutes}:${seconds} [${segment.speaker}]:\n${segment.text}\n`;
                });

                // One append at a time: two results arriving together used to read the same old
                // transcript, and the later write erased the earlier one's lines.
                appendQueueRef.current = appendQueueRef.current.then(async () => {
                    const transcriptRef = ref(database, basePath);
                    const snapshot = await get(transcriptRef);
                    const currentTranscript = (snapshot.val() || {}).completeTranscript || '';
                    await update(transcriptRef, {
                        completeTranscript: `${currentTranscript}${formattedSegments}`,
                        lastUpdated: transcriptTimestamp,
                        doctorName: doctorRef.current?.doctorName,
                        callType: callTypeRef.current
                    });
                }).catch(error => console.error('Error appending transcript:', error));
            }
        } catch (error) {
            console.error('Error handling Deepgram transcript:', error);
        }
    }, []);

    const connectSocket = useCallback(() => {
        if (!sessionActiveRef.current || stoppingRef.current) return;
        reconnectTimerRef.current = null;

        let socket;
        try {
            socket = new WebSocket(DEEPGRAM_URL, ['token', DEEPGRAM_KEY]);
        } catch (error) {
            console.error('Could not open the Deepgram connection:', error);
            reconnectAttemptRef.current += 1;
            reconnectTimerRef.current = setTimeout(connectSocket, reconnectDelay(reconnectAttemptRef.current));
            return;
        }
        socketRef.current = socket;

        socket.onopen = () => {
            if (socketRef.current !== socket) return;
            console.log('✓ Deepgram connection open');
            reconnectAttemptRef.current = 0;
            setIsLive(true);

            keepaliveIntervalRef.current = setInterval(() => {
                if (socket.readyState === WebSocket.OPEN) {
                    try { socket.send(JSON.stringify({ type: 'KeepAlive' })); } catch (e) {}
                }
            }, 5000);

            startLiveRecorder();

            if (!announcedRef.current) {
                announcedRef.current = true;
                notification.success({
                    message: "Call Transcription Started",
                    description: "Recording both participants in the call.",
                    duration: 3
                });
            }
        };

        socket.onmessage = handleDeepgramMessage;

        socket.onerror = (error) => {
            console.error('Deepgram connection error:', error);
        };

        socket.onclose = (event) => {
            if (socketRef.current !== socket) return;
            console.log('Deepgram connection closed:', event.code, event.reason);
            socketRef.current = null;
            if (keepaliveIntervalRef.current) {
                clearInterval(keepaliveIntervalRef.current);
                keepaliveIntervalRef.current = null;
            }
            stopLiveRecorder();
            setIsLive(false);

            // The call is still on: reconnect instead of giving up for the rest of it.
            if (sessionActiveRef.current && !stoppingRef.current) {
                reconnectAttemptRef.current += 1;
                const delay = reconnectDelay(reconnectAttemptRef.current);
                console.warn(`Transcription disconnected; reconnecting in ${delay / 1000}s`);
                reconnectTimerRef.current = setTimeout(connectSocket, delay);
            }
        };
    }, [DEEPGRAM_KEY, handleDeepgramMessage, startLiveRecorder]);

    const startTranscription = useCallback(async () => {
        if (sessionActiveRef.current) {
            console.log("Already transcribing, skipping start");
            return;
        }
        if (!DEEPGRAM_KEY) {
            notification.error({
                message: "Transcription Error",
                description: "Missing REACT_APP_DEEPGRAM_API_KEY",
            });
            throw new Error("Missing REACT_APP_DEEPGRAM_API_KEY");
        }

        convIdRef.current = conversationId;
        callTypeRef.current = callType;
        doctorRef.current = doctorDetail;
        messageIdRef.current = transcriptMessageKey || messageIdRef.current || `agora_transcript_${Date.now()}`;
        sessionCounterRef.current += 1;
        chunkCounterRef.current = 0;
        sessionStartTimeRef.current = Date.now();
        archiveChunksRef.current = [];
        appendQueueRef.current = Promise.resolve();
        reconnectAttemptRef.current = 0;
        announcedRef.current = false;
        stoppingRef.current = false;
        sessionActiveRef.current = true;
        setIsTranscribing(true);
        console.log(`📼 Transcription session #${sessionCounterRef.current} for ${convIdRef.current}/${messageIdRef.current}`);

        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 48000,
                latencyHint: 'interactive'
            });
            audioContextRef.current = audioContext;
            if (audioContext.state === 'suspended') {
                audioContext.resume().catch(() => {});
            }
        } catch (error) {
            sessionActiveRef.current = false;
            setIsTranscribing(false);
            notification.error({
                message: "Transcription Error",
                description: "Failed to start call transcription: " + error.message,
            });
            throw error;
        }

        // Connects now and keeps reconnecting while the call lasts; a failed first attempt is
        // retried instead of leaving the call without a transcript.
        connectSocket();
    }, [conversationId, doctorDetail, callType, transcriptMessageKey, DEEPGRAM_KEY, connectSocket]);

    const sendTranscriptToAi = async () => {
        try {
            const cid = convIdRef.current;
            const messageId = messageIdRef.current;
            if (!cid || !messageId) return;

            const transcriptRef = ref(database, `/mentalHealthTranscript/${cid}/${messageId}`);
            const currentData = (await get(transcriptRef)).val() || {};
            const completeTranscript = currentData.completeTranscript || '';
            if (!completeTranscript) {
                console.log('No complete transcript available for AI processing.');
                return;
            }

            // Fire and forget - don't wait for response
            soapNoteAIMeeting({
                chatID: cid,
                messageID: messageId,
                transcript: completeTranscript,
                sessionID: currentData.sessionID || currentData.currentSessionID || null,
                chatType: 'family_doctor'
            })
                .then(async apiResponse => {
                    await update(transcriptRef, {
                        aiProcessed: true,
                        callType: callTypeRef.current || null,
                        aiProcessingTimestamp: Date.now(),
                        summary: apiResponse?.summary || "",
                        aiTranscript: apiResponse?.transcript || completeTranscript
                    });
                })
                .catch(async error => {
                    await update(transcriptRef, {
                        aiProcessed: false,
                        aiProcessingError: error.message
                    });
                    console.error('❌ AI processing error:', error);
                });

            if (typeof window !== 'undefined') {
                localStorage.removeItem(`transcription_${cid}`);
                localStorage.removeItem(`meetmsgID_${cid}`);
            }
        } catch (error) {
            console.error('Error initiating AI transcript processing:', error);
        }
    };

    // Stable (no deps): reads the session from refs, so a chat switch does not re-create it and
    // trigger the caller's cleanup. Safe to call when nothing is running.
    const stopTranscription = useCallback(async () => {
        if (!sessionActiveRef.current || stoppingRef.current) return;
        stoppingRef.current = true;
        console.log("Stopping Agora call transcription...");

        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        if (uploadTimerRef.current) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }

        try {
            // Flush the recording's last second, then save it in the background.
            const archive = archiveRecorderRef.current;
            archiveRecorderRef.current = null;
            if (archive && archive.state !== 'inactive') {
                await new Promise((resolve) => {
                    archive.addEventListener('stop', resolve, { once: true });
                    try { archive.stop(); } catch (e) { resolve(); }
                    setTimeout(resolve, 1000);
                });
            }
            const finalAudio = [archiveChunksRef.current.slice(), sessionCounterRef.current,
                convIdRef.current, messageIdRef.current, callTypeRef.current];
            Promise.resolve(uploadingRef.current)
                .then(() => storeAudioChunkToFirebase(...finalAudio))
                .catch((error) => console.error('❌ Final call audio upload failed:', error));

            // Let Deepgram return its last words, then close.
            stopLiveRecorder();
            const socket = socketRef.current;
            if (socket && socket.readyState === WebSocket.OPEN) {
                try { socket.send(JSON.stringify({ type: 'CloseStream' })); } catch (e) {}
                await Promise.race([
                    new Promise((resolve) => socket.addEventListener('close', resolve, { once: true })),
                    wait(1500)
                ]);
            }
            socketRef.current = null;
            if (socket && socket.readyState !== WebSocket.CLOSED) {
                try { socket.close(); } catch (e) {}
            }
            if (keepaliveIntervalRef.current) {
                clearInterval(keepaliveIntervalRef.current);
                keepaliveIntervalRef.current = null;
            }

            // Every transcript line is written before the transcript is closed and sent to the AI.
            await Promise.race([appendQueueRef.current, wait(3000)]);

            if (convIdRef.current && messageIdRef.current) {
                try {
                    await update(ref(database, transcriptPath()), {
                        endTimestamp: Date.now(),
                        status: 'completed',
                        stoppedManually: true
                    });
                } catch (error) {
                    console.error("Error updating Firebase status:", error);
                }
            }

            sendTranscriptToAi();
        } catch (error) {
            console.error("Error stopping transcription:", error);
        } finally {
            if (transcriptTimeoutRef.current) {
                clearTimeout(transcriptTimeoutRef.current);
                transcriptTimeoutRef.current = null;
            }
            setLiveTranscript('');
            setInterimTranscript('');
            lastFinalTranscriptRef.current = '';

            [localStreamRef, remoteStreamRef].forEach((streamRef) => {
                if (!streamRef.current) return;
                try {
                    streamRef.current.source?.disconnect();
                    streamRef.current.gain?.disconnect();
                    streamRef.current.panner?.disconnect();
                } catch (e) {}
                streamRef.current = null;
            });
            if (mergerRef.current) {
                try { mergerRef.current.disconnect(); } catch (e) {}
                mergerRef.current = null;
            }
            if (destinationRef.current) {
                try { destinationRef.current.stream?.getTracks().forEach(track => track.stop()); } catch (e) {}
                destinationRef.current = null;
            }
            if (audioContextRef.current) {
                try {
                    if (audioContextRef.current.state !== 'closed') await audioContextRef.current.close();
                } catch (e) {}
                audioContextRef.current = null;
            }

            sessionActiveRef.current = false;
            stoppingRef.current = false;
            setIsLive(false);
            setIsTranscribing(false);
            console.log("✓ Transcription cleanup complete");
        }
    }, [storeAudioChunkToFirebase]);

    return {
        startTranscription,
        stopTranscription,
        isTranscribing,
        isLive,
        updateAudioStreams,
        liveTranscript,
        interimTranscript
    };
};
